import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Send, MessageSquare, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { postLodgement as api } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_TONE = {
  open: 'border-red-200 bg-red-50 text-red-700',
  actioned: 'border-blue-200 bg-blue-50 text-blue-700',
  awaiting_client: 'border-amber-200 bg-amber-50 text-amber-700',
  closed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};
const STATUS_ICON = { open: AlertCircle, actioned: MessageSquare, awaiting_client: Clock, closed: CheckCircle2 };

export default function PostLodgementTab({ matterId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    api.list(matterId).then(setData).finally(() => setLoading(false));
  }, [matterId]);
  React.useEffect(load, [load]);

  if (loading) return <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;

  return (
    <div data-testid="post-lodgement-tab">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-2">
          {Object.entries(data?.counts || {}).map(([status, n]) => {
            const Ic = STATUS_ICON[status];
            return (
              <Badge key={status} className={`${STATUS_TONE[status]} border`} data-testid={`pl-count-${status}`}>
                {Ic && <Ic className="mr-1 h-3 w-3" />}{status}: {n}
              </Badge>
            );
          })}
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="pl-new-btn"><Plus className="mr-2 h-4 w-4" /> New DHA request</Button>
      </div>
      {(!data?.items?.length) ? (
        <Card><CardContent className="p-8 text-center text-sm text-slate-500">No post-lodgement DHA requests yet. Log the first BVA, s57 or s56 request as it comes in.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <Card key={r.id} className={`cursor-pointer transition hover:border-slate-300 border-l-4 ${
              r.status === 'open' ? 'border-l-red-500' : r.status === 'awaiting_client' ? 'border-l-amber-500' : r.status === 'actioned' ? 'border-l-blue-500' : 'border-l-emerald-500'
            }`} onClick={() => setSelected(r.id)} data-testid={`pl-item-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className="border border-slate-200 bg-slate-100 text-slate-700 font-mono text-xs">{r.kind}</Badge>
                      <div className="font-semibold text-[#1A3A5C]">{r.title}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {r.dha_ref && <span>DHA ref: <span className="font-mono">{r.dha_ref}</span> · </span>}
                      Due {r.due_date || '—'} · {(r.thread || []).length} message{(r.thread || []).length === 1 ? '' : 's'} · Updated {new Date(r.updated_at).toLocaleDateString('en-AU')}
                    </div>
                  </div>
                  <Badge className={`${STATUS_TONE[r.status]} border`}>{r.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <NewRequestDialog matterId={matterId} kinds={data?.kinds || []} open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} />
      <RequestDetailDialog matterId={matterId} requestId={selected} items={data?.items || []} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}

function NewRequestDialog({ matterId, kinds, open, onClose, onSaved }) {
  const [form, setForm] = React.useState({ kind: 'BVA', title: '', dha_ref: '', body: '', due_date: '' });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) setForm({ kind: 'BVA', title: '', dha_ref: '', body: '', due_date: '' }); }, [open]);
  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body required'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.dha_ref) delete payload.dha_ref;
      if (!payload.due_date) delete payload.due_date;
      await api.create(matterId, payload);
      toast.success('DHA request logged'); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pl-new-dialog">
        <DialogHeader><DialogTitle>New DHA request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Request type</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="pl-new-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{kinds.map((k) => <SelectItem key={k.k} value={k.k}>{k.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Title</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="S57 re: employer nomination" data-testid="pl-new-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">DHA reference</label>
              <Input value={form.dha_ref} onChange={(e) => setForm({ ...form, dha_ref: e.target.value })} placeholder="DHA-…" data-testid="pl-new-ref" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Due date</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="pl-new-due" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">DHA request body</label>
            <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Paste DHA's request or your summary here…" data-testid="pl-new-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="pl-new-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Log request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDetailDialog({ matterId, requestId, items, onClose, onChanged }) {
  const req = items.find((r) => r.id === requestId);
  const [reply, setReply] = React.useState('');
  const [replyKind, setReplyKind] = React.useState('staff');
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState('');
  React.useEffect(() => { if (req) { setStatus(req.status); setReply(''); } }, [req]);
  if (!req) return null;
  const doReply = async () => {
    if (!reply.trim()) { toast.error('Reply body required'); return; }
    setBusy(true);
    try {
      await api.reply(matterId, req.id, { body: reply, kind: replyKind });
      setReply(''); toast.success('Reply added'); onChanged();
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };
  const updateStatus = async (newStatus) => {
    setStatus(newStatus);
    try { await api.update(matterId, req.id, { status: newStatus }); onChanged(); }
    catch { toast.error('Status update failed'); }
  };
  return (
    <Dialog open={!!requestId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pl-detail-dialog">
        <DialogHeader><DialogTitle>{req.kind} · {req.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Select value={status} onValueChange={updateStatus}>
              <SelectTrigger className="w-48" data-testid="pl-detail-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="actioned">Actioned</SelectItem>
                <SelectItem value="awaiting_client">Awaiting client</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            {req.dha_ref && <span className="text-xs text-slate-500">DHA ref: <span className="font-mono">{req.dha_ref}</span></span>}
            {req.due_date && <span className="text-xs text-slate-500">Due: {req.due_date}</span>}
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto rounded-lg border border-slate-200 p-3" data-testid="pl-thread">
            {(req.thread || []).map((t) => (
              <div key={t.id} className={`rounded-md p-3 text-sm ${
                t.kind === 'dha' ? 'bg-red-50 border border-red-100' : t.kind === 'client' ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50 border border-slate-100'
              }`} data-testid={`pl-thread-msg-${t.id}`}>
                <div className="mb-1 text-xs text-slate-500">
                  <b className="text-[#1A3A5C]">{t.by_name}</b> · <Badge className="border border-slate-200 bg-white text-slate-600 text-[10px]">{t.kind}</Badge> · {new Date(t.at).toLocaleString('en-AU')}
                </div>
                <div className="whitespace-pre-wrap text-slate-700">{t.body}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs">
              <span className="font-semibold uppercase tracking-wider text-slate-500">Add reply</span>
              <Select value={replyKind} onValueChange={setReplyKind}>
                <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff note</SelectItem>
                  <SelectItem value="client">From client</SelectItem>
                  <SelectItem value="dha">From DHA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="pl-reply-body" />
            <div className="mt-2 flex justify-end">
              <Button onClick={doReply} disabled={busy} data-testid="pl-reply-submit">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Add reply
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
